import { Subject } from 'rxjs';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { SessionService } from '@fi-app/utilities/fi-services/session.service';
import { UserService } from '@fi-app/user/user.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { ExtendedRoleRightService } from '../../role/services/extended-role-right.service';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { AuthenticateService } from '@fi-app/utilities/fi-services/authenticate.service';
import { UserForSupportSession } from '@fi-app/components/logged-in-user/user-for-support-session.iterface';
import { BehaviorSubject } from 'rxjs';
import { AccessRightFilterCollectionModel, ISiteDesign, ILightWeightObject } from '@fi-shared/models';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { takeUntil } from 'rxjs/operators';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiUserSharedService } from '@fi-shared/fi-user-shared/fi-user-shared.service';
import { FiAuthenticateSharedService } from '@fi-shared/fi-auth-shared/fi-authenticate-shared.service';
import { FiSiteDesignService } from '@fi-shared/fi-shared-services/fi-site-design.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';

@Component({
    selector: 'fi-logged-in-user',
    templateUrl: './logged-in-user.component.html',
    styleUrls: ['./logged-in-user.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class LoggedInUserComponent implements OnInit, OnDestroy {
    @Output() helpMenuOpen = new EventEmitter();
    HasBusinessUnitEditAccess = false;
    userAccessRights: AccessRightFilterCollectionModel;
    businessUnits: ILightWeightObject[] = [];
    allActiveUsers$ = new BehaviorSubject<UserForSupportSession[]>(undefined);
    resetSearchData$: Subject<void> = new Subject<void>();
    switchUnitMenuOpened$ = new Subject<void>();
    signinAsMenuOpenedOpened$ = new Subject<void>();

    FiStateNames = FiStateNames;
    availableSiteDesigns: ISiteDesign[] = [];

    constructor(
        private sessionService: SessionService,
        private userService: UserService,
        private detectDevice: DetectDevicesService,
        private extendedRoleRightService: ExtendedRoleRightService,
        private stateAccessCheckService: StateAccessCheckService,
        private authService: AuthenticateService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private fiSiteDesignService: FiSiteDesignService,
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
        private userSharedService: FiUserSharedService,
        private authenticateSharedService: FiAuthenticateSharedService,
    ) {}

    ngOnInit() {
        this.fiSignalRHelperService.initialUserAccessRights.pipe(takeUntil(this.ngOnDestroy$)).subscribe((data) => {
            this.userAccessRights = data;

            this.HasBusinessUnitEditAccess =
                this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.BusinessUnit.Edit) &&
                data.AccessRightCoreFilter.BusinessUnitIds.indexOf(this.userDataService.userData.BusinessUnitId) > -1;

            //this.HasExtendedRoleAccessRights = data.AccessRightCoreFilter.HasExtendedRoleAccessRights;
        });

        this.userDataService.onUserDataChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((userData) => {
            if (userData) {
                this.availableSiteDesigns = userData.AvailableSiteDesigns;
                this.sessionService.setSiteDesign(userData.ActiveSiteDesign);
            }
        });

        this.updateBusinessUnits();

        if (this.isSystemUser()) this.getAllUsers();
    }

    private updateBusinessUnits() {
        this.businessUnits = this.userDataService.getUserBusinessUnits().filter((unit) => unit.IsActivated);
    }

    Logout($event: Event) {
        $event.preventDefault();
        this.authenticateSharedService.logout();
    }

    CanSwitchUnit() {
        return this.businessUnits.length > 1;
    }

    // note: not used ?
    // SwitchExtendedAccess() {
    //     if (this.sessionService.getSession().IsExtendedAccessSession)
    //         this.extendedRoleRightService.closeExtendedAccessSession();
    //     else
    //         this.extendedRoleRightService.openFilter(this.userAccessRights);
    // }

    selectBusinessUnit(unit: ILightWeightObject) {
        if (unit.Id === this.userDataService.getUserBusinessUnitId()) {
            return;
        }
        this.userSharedService.saveUserPreferredBusinessUnit(unit).then(() => {
            //window.location.reload();
        });
    }

    isExtendedRightsSession() {
        return this.sessionService.getSession().IsExtendedAccessSession;
    }

    getUserName(): string {
        return this.userDataService.getUserFullName();
    }

    getBusinessUnitName(): string {
        return this.userDataService.getUserBusinessUnitName();
    }

    getUserId(): number {
        return this.userDataService.getUserId();
    }

    getBusinessUnitId(): number {
        return this.userDataService.getUserBusinessUnitId();
    }

    isShowHelpMenu() {
        return this.detectDevice.isMobilePhone();
    }

    isSystemUser() {
        return this.userDataService.canLoginSupportSession();
    }

    getAllUsers() {
        this.userService.getUsersForSupportSession().then((activeUsers) => {
            this.allActiveUsers$.next(activeUsers);
        });
    }

    switchAccount(user) {
        this.authService.LoginSupport(user.Id, this.detectDevice.isMobilePhone()).then((loggedInUser) => {
            this.userDataService.setLoggedInUserToken(loggedInUser.TokenId);
            window.open('Home', '_blank');
        });
    }

    onMatMenuItemClick($event: MouseEvent) {
        //$event.stopPropagation();
    }

    resetSearchData() {
        this.resetSearchData$.next();
    }

    showHelpUserMenuOpened() {
        this.signinAsMenuOpenedOpened$.next();
    }

    switchUnitMenuOpened() {
        this.updateBusinessUnits();
        this.switchUnitMenuOpened$.next();
    }

    changeSiteDesign(siteDesign: ISiteDesign) {
        if (!this.isActiveSiteDesign(siteDesign.Id)) {
            this.fiSiteDesignService.savePreferredSiteDesign(siteDesign).then(() => {
                this.sessionService.setSiteDesign(siteDesign);
            });
        }
    }

    isActiveSiteDesign(siteDesignId: number): boolean {
        return this.userDataService.getUserData().ActiveSiteDesign.Id === siteDesignId;
    }

    showUserEditOldMenuItem() {
        return false;
        //return this.customerDataService.isTestCustomer();
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
